import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

const ADLRecordList = ({
    records = [],
    onAddRecord,
    onEditRecord,
    onDelete,
    isLoading = false
}) => {
    const [filterText, setFilterText] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // ✅ Filtered data
    const filteredRecords = records.filter((r) =>
        Object.values(r).some((v) =>
            String(v).toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const totalRows = filteredRecords.length;
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRows);

    // ✅ Delete handler
    const handleDelete = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This ADL record will be deleted permanently!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                onDelete(id);
            }
        });
    };

    // ✅ View details handler
    const handleViewDetails = (record) => {
        Swal.fire({
            title: `ADL Record - ${record.client_name}`,
            html: `
                <div class="text-start">
                    <p><strong>Client Name:</strong> ${record.client_name}</p>
                    <p><strong>Room Number:</strong> ${record.room_number}</p>
                    <p><strong>Date Range:</strong> ${record.date_range}</p>
                    <p><strong>Checked By:</strong> ${record.checked_by}</p>
                    <p><strong>Admission Date:</strong> ${record.admission_date}</p>
                    <p><strong>Activities Recorded:</strong> ${record.activities_count}</p>
                </div>
            `,
            icon: 'info',
            confirmButtonText: 'Close'
        });
    };

    // ✅ Export PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("ADL Records Report", 14, 10);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 18);

        const tableColumn = ["#", "Client Name", "Room No", "Date Range", "Checked By", "Activities"];
        const tableRows = filteredRecords.map((r, index) => [
            index + 1,
            r.client_name,
            r.room_number,
            r.date_range,
            r.checked_by,
            r.activities_count
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 25,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 135, 245] }
        });
        doc.save("adl_records_report.pdf");
    };

    // ✅ Export CSV data preparation
    const getCSVData = () => {
        return filteredRecords.map((record, index) => ({
            "S.No": index + 1,
            "Client Name": record.client_name,
            "Room Number": record.room_number,
            "Date Range": record.date_range,
            "Checked By": record.checked_by,
            "Admission Date": record.admission_date,
            "Activities Count": record.activities_count
        }));
    };

    // ✅ Custom pagination
    const CustomPagination = ({
        onChangePage,
        onChangeRowsPerPage,
        currentPage,
        rowsPerPage,
        rowCount,
    }) => {
        const totalPages = Math.ceil(rowCount / rowsPerPage);
        return (
            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: "0.875rem" }}>
                        Rows per page:
                    </span>
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={rowsPerPage}
                        onChange={(e) =>
                            onChangeRowsPerPage(Number(e.target.value), currentPage)
                        }
                    >
                        {[5, 10, 15, 20].map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="d-flex align-items-center gap-1">
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage - 1, rowCount)}
                        disabled={currentPage === 1}
                    >
                        <i className="ri-arrow-left-s-line"></i>
                    </button>
                    <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="btn btn-sm border"
                        onClick={() => onChangePage(currentPage + 1, rowCount)}
                        disabled={currentPage === totalPages}
                    >
                        <i className="ri-arrow-right-s-line"></i>
                    </button>
                </div>
            </div>
        );
    };

    // ✅ Columns
    const columns = [
        {
            name: "S.No",
            cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
            width: "80px",
        },
        {
            name: "Client Name",
            selector: (r) => r.client_name,
            sortable: true,
            wrap: true
        },
        {
            name: "Room No",
            selector: (r) => r.room_number,
            sortable: true,
            width: "100px"
        },
        {
            name: "Date Range",
            selector: (r) => r.date_range,
            wrap: true
        },
        {
            name: "Checked By",
            selector: (r) => r.checked_by,
            wrap: true
        },
        {
            name: "Activities",
            selector: (r) => r.activities_count,
            width: "100px",
            center: true
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-1">
                    {onEditRecord && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="Edit"
                            onClick={() => onEditRecord(row.id)}
                        >
                            <i className="ri-edit-line"></i>
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </button>
                    )}
                </div>
            ),
            width: "180px",
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <div>
            {/* Header Section */}
            <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                {/* Export + Search */}
                <div className="d-flex align-items-center gap-2 position-relative">
                    <div className="dropdown">
                        <button
                            className="btn btn-label-primary btn-sm dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={isLoading || filteredRecords.length === 0}
                        >
                            <i className="ri-download-2-line me-1"></i> Export
                        </button>
                        <ul className="dropdown-menu shadow-sm">
                            <li>
                                <button
                                    className="dropdown-item"
                                    onClick={handleExportPDF}
                                    disabled={filteredRecords.length === 0}
                                >
                                    <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                                </button>
                            </li>
                            <li>
                                <CSVLink
                                    data={getCSVData()}
                                    filename="adl_records.csv"
                                    className="dropdown-item"
                                    onClick={() => {
                                        if (filteredRecords.length === 0) {
                                            Swal.fire('Info', 'No data to export', 'info');
                                            return false;
                                        }
                                    }}
                                >
                                    <i className="ri-file-excel-line me-1 text-success"></i> CSV
                                </CSVLink>
                            </li>
                        </ul>
                    </div>

                    <input
                        type="text"
                        placeholder="Search ADL Records..."
                        className="form-control form-control-sm"
                        style={{ width: "220px" }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                {/* Center Info */}
                <h6 className="mb-0 text-center text-muted flex-grow-1">
                    {isLoading ? (
                        <span>Loading records...</span>
                    ) : (
                        <span>Showing {start}-{end} of {totalRows} records</span>
                    )}
                </h6>

                {/* Add Button */}
                <div>
                    {onAddRecord && (
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={onAddRecord}
                            disabled={isLoading}
                        >
                            <i className="ri-add-line me-1"></i> Add New
                        </button>
                    )}
                </div>
            </div>

            {/* Table Section */}
            <div className="card-body p-2">
                <DataTable
                    columns={columns}
                    data={filteredRecords}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationTotalRows={totalRows}
                    onChangeRowsPerPage={setRowsPerPage}
                    onChangePage={setCurrentPage}
                    paginationComponent={CustomPagination}
                    highlightOnHover
                    striped
                    responsive
                    progressPending={isLoading}
                    progressComponent={
                        <div className="d-flex justify-content-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    }
                    noDataComponent={
                        <div className="p-3 text-muted text-center">
                            {isLoading ? "Loading records..." : "No ADL records found"}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

export default ADLRecordList;